import type { ActionDefinition, TutorialEngine as ITutorialEngine, ActionType } from '../types';
import type { ActionRegistry, ActionContext, ActionHandler } from '../registry/ActionRegistry';

/**
 * Result of executing an action
 */
export interface ActionExecutionResult {
  success: boolean;
  error?: Error;
  attempts: number;
  duration: number; // milliseconds
}

/**
 * Configuration for the tutorial engine
 */
export interface TutorialEngineConfig {
  /**
   * Maximum retry attempts for failed actions
   */
  maxRetries?: number;

  /**
   * Initial delay between retries (ms)
   */
  retryDelayMs?: number;

  /**
   * Exponential backoff multiplier
   */
  retryBackoffMultiplier?: number;

  /**
   * Callback for analytics/logging
   */
  onActionStart?: (action: ActionDefinition) => void;
  onActionSuccess?: (action: ActionDefinition, result: ActionExecutionResult) => void;
  onActionError?: (action: ActionDefinition, error: Error, result: ActionExecutionResult) => void;
}

/**
 * Tutorial Engine - Executes actions with retry logic
 * Orchestrates action handlers registered in the ActionRegistry
 */
export class TutorialEngine implements ITutorialEngine {
  private registry: ActionRegistry;
  private context: ActionContext;
  private config: Required<TutorialEngineConfig>;

  constructor(
    registry: ActionRegistry,
    context: ActionContext,
    config: TutorialEngineConfig = {}
  ) {
    this.registry = registry;
    this.context = context;
    this.config = {
      maxRetries: config.maxRetries ?? 3,
      retryDelayMs: config.retryDelayMs ?? 500,
      retryBackoffMultiplier: config.retryBackoffMultiplier ?? 1.5,
      onActionStart: config.onActionStart ?? (() => {}),
      onActionSuccess: config.onActionSuccess ?? (() => {}),
      onActionError: config.onActionError ?? (() => {}),
    };
  }

  /**
   * Execute a single action with retry logic
   */
  async executeAction(action: ActionDefinition): Promise<void> {
    const startTime = performance.now();
    let lastError: Error | null = null;
    let attempts = 0;

    // Get handler
    const handler = this.registry.get(action.type);
    if (!handler) {
      throw new Error(`No handler registered for action type: ${action.type}`);
    }

    // Validate action if handler provides validation
    if (handler.validate && !handler.validate(action)) {
      throw new Error(`Invalid action configuration for type: ${action.type}`);
    }

    // Determine retry config
    const maxAttempts = action.retryConfig?.maxAttempts ?? this.config.maxRetries;
    const useExponentialBackoff = action.retryConfig?.exponentialBackoff ?? true;

    this.config.onActionStart(action);

    // Retry loop
    while (attempts < maxAttempts) {
      try {
        await handler.execute(action, this.context);

        // Success!
        const duration = performance.now() - startTime;
        const result: ActionExecutionResult = {
          success: true,
          attempts: attempts + 1,
          duration,
        };

        this.config.onActionSuccess(action, result);
        return;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        attempts++;

        // If we have more attempts, wait before retrying
        if (attempts < maxAttempts) {
          const baseDelay = action.retryConfig?.delayMs ?? this.config.retryDelayMs;
          const delay = useExponentialBackoff
            ? baseDelay * Math.pow(this.config.retryBackoffMultiplier, attempts - 1)
            : baseDelay;

          await this.context.timerAdapter.delay(delay);
        }
      }
    }

    // All retries exhausted
    const duration = performance.now() - startTime;
    const result: ActionExecutionResult = {
      success: false,
      error: lastError!,
      attempts,
      duration,
    };

    this.config.onActionError(action, lastError!, result);
    throw new Error(lastError?.message || 'Action execution failed');
  }

  /**
   * Execute a sequence of actions
   * Stops on first error unless configured otherwise
   */
  async executeActions(actions: ActionDefinition[]): Promise<void> {
    for (const action of actions) {
      await this.executeAction(action);
    }
  }

  /**
   * Register a new action handler
   */
  registerActionHandler(type: ActionType, handler: ActionHandler): void {
    this.registry.register(type, handler);
  }

  /**
   * Unregister an action handler
   */
  unregisterActionHandler(type: ActionType): void {
    this.registry.unregister(type);
  }

  /**
   * Update the adapter context
   */
  updateContext(context: Partial<ActionContext>): void {
    this.context = { ...this.context, ...context };
  }
}
